import { getRuntimeConfig, sprintLimit } from "./runtime-config.js";

const API_VERSION = "7.1";
const INCLUDED_TYPES = ["User Story", "Product Backlog Item"];
const EXCLUDED_STATES = new Set(["new", "refined"]);

export { sprintLimit };

function config(teamOverride) {
  const runtime = getRuntimeConfig();
  if (!runtime.pat && !runtime.serverManagedAuth) {
    throw new Error("Falta el PAT de Azure DevOps.");
  }
  return {
    org: runtime.org,
    project: runtime.project,
    team: teamOverride || runtime.team,
    pat: runtime.pat,
  };
}

function encodeBasic(pat) {
  const raw = `:${pat}`;
  if (typeof btoa === "function") return btoa(raw);
  return Buffer.from(raw).toString("base64");
}

function authHeaders(pat) {
  const headers = { "Content-Type": "application/json" };
  if (pat && pat !== "managed") {
    headers.Authorization = `Basic ${encodeBasic(pat)}`;
  }
  return headers;
}

async function azureFetch(cfg, path, options = {}) {
  const runtime = getRuntimeConfig();
  const target = `https://dev.azure.com/${encodeURIComponent(cfg.org)}/${path}`;
  const url = runtime.proxyUrl
    ? `${String(runtime.proxyUrl).replace(/\/$/, "")}?url=${encodeURIComponent(target)}`
    : target;
  try {
    const response = await fetch(url, {
      ...options,
      headers: { ...authHeaders(cfg.pat), ...options.headers },
    });
    if (!response.ok) {
      const detail = await response.text();
      throw new Error(`Azure DevOps respondió ${response.status}: ${detail.slice(0, 500)}`);
    }
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("json")) {
      throw new Error(
        "La respuesta no es JSON. Revisa la URL del proxy y recarga la página para cargar la versión actual de la aplicación.",
      );
    }
    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(
        "El navegador bloqueó la consulta a Azure DevOps (CORS). En GitHub Pages indica la URL del proxy desplegado desde worker/index.js.",
      );
    }
    throw error;
  }
}

function teamBase(cfg) {
  return `${encodeURIComponent(cfg.project)}/${encodeURIComponent(cfg.team)}`;
}

export async function getIterations(team, { limit } = {}) {
  const cfg = config(team);
  const data = await azureFetch(
    cfg,
    `${teamBase(cfg)}/_apis/work/teamsettings/iterations?api-version=${API_VERSION}`,
  );
  const iterations = (data.value || [])
    .map((iteration) => ({
      id: iteration.id,
      name: iteration.name,
      path: iteration.path,
      startDate: iteration.attributes?.startDate || null,
      finishDate: iteration.attributes?.finishDate || null,
      timeFrame: iteration.attributes?.timeFrame || null,
    }))
    .sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0));
  if (!limit) return iterations;
  // Un sprint futuro aún no tiene trabajo que revisar.
  const reviewable = iterations.filter((iteration) => iteration.timeFrame !== "future");
  return (reviewable.length ? reviewable : iterations).slice(0, limit);
}

async function getTeamAreaPaths(cfg) {
  const data = await azureFetch(
    cfg,
    `${teamBase(cfg)}/_apis/work/teamsettings/teamfieldvalues?api-version=${API_VERSION}`,
  );
  const values = [data.defaultValue, ...(data.values || []).map((item) => item.value)].filter(Boolean);
  return [...new Set(values)];
}

function escapeWiql(value) {
  return String(value).replaceAll("'", "''");
}

async function queryIterationIds(cfg, iterationPath) {
  const areaPaths = await getTeamAreaPaths(cfg);
  const areaClause = areaPaths.length
    ? ` AND (${areaPaths.map((path) => `[System.AreaPath] UNDER '${escapeWiql(path)}'`).join(" OR ")})`
    : "";
  const typeClause = INCLUDED_TYPES.map(
    (type) => `[System.WorkItemType] = '${type}'`,
  ).join(" OR ");
  const query = `SELECT [System.Id] FROM WorkItems WHERE [System.TeamProject] = '${escapeWiql(cfg.project)}' AND [System.IterationPath] = '${escapeWiql(iterationPath)}' AND (${typeClause})${areaClause}`;
  const data = await azureFetch(
    cfg,
    `${encodeURIComponent(cfg.project)}/_apis/wit/wiql?api-version=${API_VERSION}`,
    { method: "POST", body: JSON.stringify({ query }) },
  );
  return (data.workItems || []).map((item) => item.id);
}

async function getWorkItems(cfg, ids) {
  if (!ids.length) return [];
  const chunks = [];
  for (let index = 0; index < ids.length; index += 200) chunks.push(ids.slice(index, index + 200));
  const results = await Promise.all(
    chunks.map((chunk) =>
      azureFetch(
        cfg,
        `${encodeURIComponent(cfg.project)}/_apis/wit/workitems?ids=${chunk.join(",")}&api-version=${API_VERSION}`,
      ),
    ),
  );
  return results.flatMap((result) => result.value || []);
}

function stripHtml(value = "") {
  return String(value)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function truncate(value, max = 220) {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).replace(/\s+\S*$/, "")}…`;
}

function effort(fields) {
  return Number(
    fields["Microsoft.VSTS.Scheduling.StoryPoints"] ??
      fields["Microsoft.VSTS.Scheduling.Effort"] ??
      0,
  );
}

function isCompleted(state) {
  const configured = (getRuntimeConfig().completedStates || "Done,Closed,Resolved,Removed")
    .split(",")
    .map((value) => value.trim().toLowerCase());
  return configured.includes(String(state).toLowerCase());
}

async function buildSprintData(cfg, iteration) {
  const ids = await queryIterationIds(cfg, iteration.path);
  const rawItems = await getWorkItems(cfg, ids);
  const included = rawItems.filter(
    (item) => !EXCLUDED_STATES.has(String(item.fields["System.State"]).toLowerCase()),
  );
  const parentIds = [
    ...new Set(included.map((item) => item.fields["System.Parent"]).filter(Boolean)),
  ];
  const parents = new Map(
    (await getWorkItems(cfg, parentIds)).map((item) => [
      item.id,
      item.fields["System.Title"],
    ]),
  );
  const runtime = getRuntimeConfig();
  const classificationField = runtime.classificationField;
  const summaryField = runtime.summaryField;
  const goalField = runtime.sprintGoalField;
  const items = included.map((item) => {
    const fields = item.fields;
    const title = stripHtml(fields["System.Title"]);
    const description = stripHtml(fields["System.Description"]);
    return {
      id: item.id,
      title,
      state: fields["System.State"],
      parent: parents.get(fields["System.Parent"]) || "Necesidad no asociada",
      classification: fields[classificationField] || "Sin clasificación",
      effort: effort(fields),
      completed: isCompleted(fields["System.State"]),
      summary: truncate(stripHtml(fields[summaryField]) || title, 180),
      benefit: truncate(description || title, 240),
    };
  });
  const classifications = {};
  for (const item of items) {
    classifications[item.classification] =
      (classifications[item.classification] || 0) + item.effort;
  }
  return {
    iteration,
    sprintGoal:
      rawItems.map((item) => stripHtml(item.fields[goalField])).find(Boolean) || "",
    items,
    indicators: {
      totalItems: items.length,
      planned: items.reduce((total, item) => total + item.effort, 0),
      executed: items
        .filter((item) => item.completed)
        .reduce((total, item) => total + item.effort, 0),
      classifications,
    },
  };
}

export async function getReviewData({ team, iterationId }) {
  const cfg = config(team);
  const iterations = await getIterations(cfg.team);
  const selected = iterations.find((iteration) => iteration.id === iterationId);
  if (!selected) throw new Error("El sprint seleccionado no pertenece al equipo indicado.");
  const eligible = iterations
    .filter(
      (iteration) =>
        iteration.finishDate &&
        (!selected.finishDate ||
          new Date(iteration.finishDate) <= new Date(selected.finishDate)),
    )
    .sort((a, b) => new Date(a.finishDate) - new Date(b.finishDate))
    .slice(-sprintLimit());
  const [current, ...historyData] = await Promise.all([
    buildSprintData(cfg, selected),
    ...eligible
      .filter((iteration) => iteration.id !== selected.id)
      .map((iteration) => buildSprintData(cfg, iteration)),
  ]);
  const history = historyData
    .map((sprint) => ({
      name: sprint.iteration.name,
      velocity: sprint.indicators.executed,
      finishDate: sprint.iteration.finishDate,
    }))
    .concat({
      name: current.iteration.name,
      velocity: current.indicators.executed,
      finishDate: current.iteration.finishDate,
    })
    .sort((a, b) => new Date(a.finishDate) - new Date(b.finishDate));
  return { ...current, team: cfg.team, history };
}
